import React from 'react';
import FlowIconSquare from '@Client/runner.directives/common/FlowIconSquare/FlowIconSquare';
import { StepIntegrationState } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { useService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import FlowModelSummaryTag from '@Client/runner.shared/FlowModelSummaryTag/FlowModelSummaryTag';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import { IFlowForUserPascalCase } from '@Shared.Angular/@types/core/contracts/queryModel/flows/flowForUser';

type Props = {
  isMobile: boolean;
  flow: IFlowForUserPascalCase;
  onHeaderClick: (flow: IFlowForUserPascalCase) => void;
};

const FlowHeader = ({ isMobile, flow, onHeaderClick }: Props) => {
  const appConfig = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');
  const runnerFlowsFormatter = useService<RunnerFlowsFormatterService>(
    'runnerFlowsFormatter'
  );
  const showConfidentialFlowOption = appConfig.showConfidentialFlowOption;
  const progress = flow.PercentageComplete;
  const webhookIconPath = ASSETS_PATH + '/webhooks-icon.svg';
  const flowHasProcessingWebhook =
    flow.IntegrationState === StepIntegrationState.Processing;
  let startedByText = '';

  if (flow.FlowId == EmptyGuid || flow.FlowId == null) {
    startedByText = 'Started by';
  } else {
    startedByText = runnerFlowsFormatter.getStartedByText(flow);
  }

  const renderProgressBar = () => (
    <div
      className="flow-status-progress--container"
      title={`${progress}%`}
      style={{ width: '100%', maxWidth: '100px' }}
    >
      <div
        className="flow-status-progress--bar"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );

  const renderCommentBubble = () =>
    flow.CommentCount > 0 && (
      <span className="flow-list__contents__subject__comment-bubble">
        <i className="fa-light fa-comments fa-lg"></i>
        <span className="flow-list__contents__subject__comment-bubble__text">
          {flow.CommentCount}
        </span>
      </span>
    );

  const renderAttachmentIcon = () =>
    flow.FilesCount > 0 && (
      <span className="flow-list__contents__subject__attachment">
        <i className="fa-light fa-paperclip fa-lg"></i>
      </span>
    );

  const renderWebhookIcon = () =>
    flowHasProcessingWebhook && (
      <img
        src={webhookIconPath}
        className="flow-list__contents__current-step__webhook"
        alt="Webhook Icon"
        title="Integration in progress"
      />
    );

  const renderDueDate = () => {
    if (flow.IsFinalised) {
      return (
        <>
          <div className="flow-list__contents__due-date__text">
            {flow.FinalisedReason === 'Withdrawn'
              ? 'Cancelled'
              : flow.FinalisedReason}{' '}
            {flow.MomentFinalised}
            <div
              className="flow-list__contents__subject__started-by"
              style={{ marginTop: '2px' }}
            >
              {flow.MomentFinalisedInLocalFormat}
            </div>
          </div>
        </>
      );
    }
    if (flow.IsOverdue) {
      return (
        <div className="flow-list__contents__due-date__text color-warning">
          <i className="fa-light fa-calendar-xmark"></i> {flow.MomentDue}
        </div>
      );
    }
    return (
      <div className="flow-list__contents__due-date__text">
        {flow.MomentDue !== 'No Due Date' && flow.MomentDue !== '' && (
          <i className="fa-light fa-calendar-check"></i>
        )}
        {flow.MomentDue}
      </div>
    );
  };

  const renderDueBy = () =>
    flow.MomentDue !== 'No Due Date' && (
      <div className="flow-list__contents__due-date__due-by">
        {flow.IsFinalised
          ? flow.FinalisedInLocalFormat
          : flow.DueDateInLocalFormat}
      </div>
    );

  const renderWorkflowType = () =>
    showConfidentialFlowOption && (
      <div className="flow-list__contents__workflow-type__block">
        <div className="flow-list__contents__workflow-type__header">
          Flow Type
        </div>
        <div className="flow-list__contents__workflow-type__text">
          <div
            className={`${
              flow.IsConfidential
                ? 'flow-list__contents__workflow-type__confidential'
                : 'flow-list__contents__workflow-type__normal'
            } flex-column`}
          >
            {flow.IsConfidential ? 'Confidential Workflow' : 'Workflow'}
          </div>
        </div>
      </div>
    );

  if (!isMobile) {
    return (
      <div
        className="flow-header flex flex-row"
        onClick={() => onHeaderClick(flow)}
      >
        <FlowIconSquare name={flow.Name} flowCode="" />

        <div className="flow-header__right-container">
          <div className="flow-list__contents flex flex-row">
            <div className="flow-list__contents__flow-status">
              <div className="flow-list__contents__flow-status__identifier">
                {flow.FlowIdentifier}
              </div>
              <div className="flow-list__contents__flow-status__progress">
                {renderProgressBar()}
              </div>
            </div>

            <div className="flow-list__contents__subject">
              <div className="flow-list__contents__subject__text">
                <span className="flow-list__contents__subject__text2">
                  {flow.Subject}
                </span>
                {renderCommentBubble()}
                {renderAttachmentIcon()}
              </div>
              <div className="flow-list__contents__subject__started-by">
                {startedByText}
              </div>
            </div>

            {(!flow.Steps || (flow.Steps && flow.IsFinalised)) && (
              <div className="flow-list__contents__current-step">
                {!flow.IsFinalised && (
                  <>
                    <div className="flow-list__contents__current-step__text">
                      <span>
                        {flow.CurrentStepsHeader} {renderWebhookIcon()}
                      </span>
                    </div>
                    <div className="flow-list__contents__current-step__flowed-to">
                      Flowed to {flow.WaitingForName}{' '}
                      {flow.MomentWaitingForLabel} {flow.MomentWaitingFor}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flow-list__contents__due-date">
              {renderDueDate()}
              {renderDueBy()}
            </div>

            {renderWorkflowType()}
          </div>
          {flow.ParsedTags?.length > 0 && (
            <div className="flow-header-right__tag-row">
              <FlowModelSummaryTag
                tags={flow.ParsedTags.map((tag) => ({
                  id: tag.Id,
                  name: tag.Name,
                  color: tag.Color
                }))}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flow-header isMobile" onClick={() => onHeaderClick(flow)}>
      <div className="flex flow-header__main">
        <FlowIconSquare name={flow.Name} flowCode="" />
        <div className="flow-list__contents flex flex-row">
          <div className="flow-list__contents__header--mobile">
            <div className="flow-list__contents__flow-name--mobile">
              <div className="flow-list__contents__flow-name__text flow-list__contents__flow-name__text--mobile">
                {flow.FlowIdentifier}
              </div>
            </div>
            <div className="flow-list__contents__flow-status--mobile">
              <div className="flow-list__contents__flow-status__progress">
                {renderProgressBar()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flow-header__extra">
        <div className="flow-list__contents__subject__text flex-row">
          {flow.Subject} {renderCommentBubble()}
          {renderAttachmentIcon()}
        </div>
        <div className="flow-list__contents__subject__started-by flow-list__contents__subject__started-by--mobile flex-row">
          {startedByText}
        </div>
        <div className="flow-list__contents__current-step__text flow-list__contents__current-step__text--mobile flex-row">
          <span>
            {flow.CurrentStepsHeader}
            {renderWebhookIcon()}
          </span>
        </div>
        <div className="flow-list__contents__current-step__flowed-to flow-list__contents__current-step__flowed-to--mobile flex-row">
          Waiting on {flow.WaitingForName} {flow.MomentWaitingFor}
        </div>
        <div className="flow-list__contents__due__type--mobile flex flex-row">
          <div className="flow-list__contents__due--mobile">
            {flow.IsFinalised && (
              <div className="flow-list__contents__due-date__text flow-list__contents__due-date__text--mobile">
                {flow.FinalisedReason} {flow.MomentFinalised}
                <div className="flow-list__contents__subject__started-by">
                  {flow.MomentFinalisedInLocalFormat}
                </div>
              </div>
            )}

            {!flow.IsFinalised && flow.IsOverdue && (
              <div className="flow-list__contents__due-date__text flow-list__contents__due-date__text--mobile color-warning flex-row">
                <i
                  className="fa-light fa-calendar-xmark"
                  aria-hidden="true"
                ></i>{' '}
                {flow.MomentDue}
              </div>
            )}

            {!flow.IsFinalised && !flow.IsOverdue && flow.MomentDue && (
              <div className="flow-list__contents__due-date__text flow-list__contents__due-date__text--mobile">
                {flow.MomentDue !== 'No Due Date' && (
                  <i
                    className="fa-light fa-calendar-check"
                    aria-hidden="true"
                  ></i>
                )}
                {flow.MomentDue}
              </div>
            )}

            {flow.MomentDue !== 'No Due Date' && (
              <div className="flow-list__contents__due-date__due-by">
                {flow.IsFinalised
                  ? flow.FinalisedInLocalFormat
                  : flow.DueDateInLocalFormat}
              </div>
            )}
          </div>

          {showConfidentialFlowOption && (
            <div>
              <div className="flow-list__contents__workflow-type__header--mobile">
                Flow Type
              </div>

              <div className="flow-list__contents__workflow-type__text">
                <div
                  className={
                    flow.IsConfidential
                      ? 'flow-list__contents__workflow-type__confidential'
                      : 'flow-list__contents__workflow-type__normal'
                  }
                >
                  {flow.IsConfidential ? 'Confidential Workflow' : 'Workflow'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlowHeader;
